
import { Component, Vue } from 'vue-property-decorator';
import DriverRepository from '@/api/repositories/DriverRepository';
import TaxiRepository from '@/api/repositories/TaxiRepository';
import StatisticsRepository from '@/api/repositories/StatisticsRepository';
import { RepositoryFactory } from '@/api/RepositoryFactory';
import Driver from '@/models/Driver';
import debounce, { DebouncedFunction } from '@/misc/debounce';

const driverRepository: DriverRepository = RepositoryFactory.get('driver');
const taxiRepository: TaxiRepository = RepositoryFactory.get('taxi');
const statisticsRepository: StatisticsRepository = RepositoryFactory.get('statistics');

/**
 * How long typing has to settle before a search fires on its own. Both of
 * these searches hit the server, so this is a deliberate trade of latency
 * against load rather than a cosmetic delay.
 */
const SEARCH_DEBOUNCE_MS = 400;

/**
 * Shortest query that may fire automatically. The buttons stay bound to the
 * original validity rules, so a deliberate press can still run a shorter
 * search than the typing-triggered path will.
 */
const MIN_AUTO_SEARCH_LENGTH = 3;

/**
 * indices of the tabs shown in this view
 */
enum SearchTab {
    DRIVERS = 0,
    TAXIS = 1,
    CANCELLATIONS = 2,
    TOP_DRIVERS = 3,
    DRIVER_CANCELLATIONS = 4
}

@Component({
    components: {
        DriverTableComponent: () => import(
            /* webpackChunkName: "DriverTableComponent" */
            '@/components/DriverTable.component.vue')
    }
})
export default class SearchDriverView extends Vue {
    public activeTab: number = SearchTab.DRIVERS;

    /* driver search tab */
    public query: string = '';
    public drivers: Driver[] = [];
    public isLoading: boolean = false;

    /* taxi search tab */
    public taxiQuery: string = '';
    public activeTaxisOnly: boolean = false;
    public taxiRows: any[] = [];
    public isLoadingTaxis: boolean = false;

    /**
     * Monotonic counters identifying the most recent search of each kind.
     * Typing can leave several requests in flight at once and they do not
     * necessarily come back in order, so each result is dropped unless its
     * token is still the current one.
     */
    private driverSearchToken: number = 0;
    private taxiSearchToken: number = 0;

    private debouncedDriverSearch: DebouncedFunction = debounce(() => this.searchDriver(true), SEARCH_DEBOUNCE_MS);
    private debouncedTaxiSearch: DebouncedFunction = debounce(() => this.searchTaxis(true), SEARCH_DEBOUNCE_MS);

    /* most cancellations tab */
    public cancellationOrder: string = 'desc';
    public cancellationRows: any[] = [];
    public isLoadingCancellations: boolean = false;
    private cancellationsLoaded: boolean = false;

    /* top drivers tab */
    public driverTripOrder: string = 'desc';
    public driverTripRows: any[] = [];
    public isLoadingDriverTrips: boolean = false;
    private driverTripsLoaded: boolean = false;

    /* most cancelled drivers tab */
    public driverCancellationOrder: string = 'desc';
    public driverCancellationRows: any[] = [];
    public isLoadingDriverCancellations: boolean = false;
    private driverCancellationsLoaded: boolean = false;

    public taxiHeaders = [
        { text: this.$t('SEARCH.TAXI.TABLE.TAXINO'), value: 'taxiNo', sortable: true },
        { text: this.$t('SEARCH.TAXI.TABLE.PLATE'), value: 'plate', sortable: true },
        { text: this.$t('SEARCH.TAXI.TABLE.SEATS'), value: 'seats', sortable: false },
        { text: this.$t('SEARCH.TAXI.TABLE.TYPE'), value: 'type', sortable: false },
        { text: this.$t('SEARCH.TAXI.TABLE.COMPANY'), value: 'company', sortable: true }
    ];

    public cancellationHeaders = [
        { text: this.$t('SEARCH.CANCELLATIONS.TABLE.TAXINO'), value: 'taxiNo', sortable: false },
        { text: this.$t('SEARCH.CANCELLATIONS.TABLE.PRIVATE_DRIVE'), value: 'privateDrive', sortable: false },
        { text: this.$t('SEARCH.CANCELLATIONS.TABLE.PLATE'), value: 'plate', sortable: false },
        { text: this.$t('SEARCH.CANCELLATIONS.TABLE.COMPANY'), value: 'company', sortable: false },
        { text: this.$t('SEARCH.CANCELLATIONS.TABLE.CANCELLATIONS'), value: 'cancellations', sortable: false }
    ];

    public driverTripHeaders = [
        { text: this.$t('SEARCH.TOP_DRIVERS.TABLE.SERIAL'), value: 'serialId', sortable: false },
        { text: this.$t('SEARCH.TOP_DRIVERS.TABLE.NAME'), value: 'name', sortable: false },
        { text: this.$t('SEARCH.TOP_DRIVERS.TABLE.PHONE'), value: 'phone', sortable: false },
        { text: this.$t('SEARCH.TOP_DRIVERS.TABLE.COMPANY'), value: 'company', sortable: false },
        { text: this.$t('SEARCH.TOP_DRIVERS.TABLE.TAXINO'), value: 'taxiNo', sortable: false },
        { text: this.$t('SEARCH.TOP_DRIVERS.TABLE.PRIVATE_DRIVE'), value: 'privateDrive', sortable: false },
        { text: this.$t('SEARCH.TOP_DRIVERS.TABLE.TRIPS'), value: 'trips', sortable: false }
    ];

    public driverCancellationHeaders = [
        { text: this.$t('SEARCH.DRIVER_CANCELLATIONS.TABLE.SERIAL'), value: 'serialId', sortable: false },
        { text: this.$t('SEARCH.DRIVER_CANCELLATIONS.TABLE.NAME'), value: 'name', sortable: false },
        { text: this.$t('SEARCH.DRIVER_CANCELLATIONS.TABLE.PHONE'), value: 'phone', sortable: false },
        { text: this.$t('SEARCH.DRIVER_CANCELLATIONS.TABLE.COMPANY'), value: 'company', sortable: false },
        { text: this.$t('SEARCH.DRIVER_CANCELLATIONS.TABLE.TAXINO'), value: 'taxiNo', sortable: false },
        { text: this.$t('SEARCH.DRIVER_CANCELLATIONS.TABLE.PRIVATE_DRIVE'), value: 'privateDrive', sortable: false },
        { text: this.$t('SEARCH.DRIVER_CANCELLATIONS.TABLE.CANCELLATIONS'), value: 'cancellations', sortable: false }
    ];

    public async onTabChanged(tab: number) {
        if (tab === SearchTab.CANCELLATIONS && !this.cancellationsLoaded) {
            await this.loadCancellations();
        }
        if (tab === SearchTab.TOP_DRIVERS && !this.driverTripsLoaded) {
            await this.loadDriverTrips();
        }
        if (tab === SearchTab.DRIVER_CANCELLATIONS && !this.driverCancellationsLoaded) {
            await this.loadDriverCancellations();
        }
    }

    /**
     * Runs on every keystroke. Below the minimum length there is nothing worth
     * asking the server, so drop any pending search and clear the table —
     * bumping the token first so a slower in-flight request cannot refill it.
     */
    public onDriverQueryInput() {
        if (!this.isAutoSearchableQuery) {
            this.debouncedDriverSearch.cancel();
            this.driverSearchToken++;
            this.drivers = [];
            this.isLoading = false;
            return;
        }
        this.debouncedDriverSearch();
    }

    /**
     * @param auto whether typing triggered this rather than the button. Errors
     * are swallowed when it did: half-typed queries fail routinely and would
     * otherwise stack up error toasts as the user types.
     *
     * Template call sites must keep the explicit `searchDriver()` parentheses.
     * Bare `@click="searchDriver"` hands Vue's native event in as `auto`, which
     * is truthy, and would silently mute the toasts on manual presses too.
     */
    public async searchDriver(auto: boolean = false) {
        if (!this.isValidSerial) return;

        const token = ++this.driverSearchToken;
        this.isLoading = true;
        try {
            const res = await driverRepository.searchDriver(this.query);
            if (token !== this.driverSearchToken) return;
            this.drivers = Driver.parseFromArray(res.data) as Driver[];
        } catch (e) {
            if (token !== this.driverSearchToken) return;
            if (!auto) this.$notifyErrorSimplified('SEARCH_DRIVER.INVALID_SYMBOL');
            this.drivers = [];
        } finally {
            if (token === this.driverSearchToken) this.isLoading = false;
        }
    }

    public get isValidSerial(): boolean {
        return this.query.length >= 3;
    }

    public get isAutoSearchableQuery(): boolean {
        return this.query.trim().length >= MIN_AUTO_SEARCH_LENGTH;
    }

    public async recycleDriver(driver: Driver) {
        await driverRepository.recycle({ id: driver.id });
        await this.searchDriver();
    }

    public get isValidTaxiQuery(): boolean {
        return this.taxiQuery.trim().length > 0;
    }

    /**
     * Deliberately stricter than `isValidTaxiQuery`, which still governs the
     * button. Taxi numbers can legitimately be one or two characters, so a
     * manual press must keep working for them; only the automatic path waits
     * for enough input to be worth a query.
     */
    public get isAutoSearchableTaxiQuery(): boolean {
        return this.taxiQuery.trim().length >= MIN_AUTO_SEARCH_LENGTH;
    }

    public onTaxiQueryInput() {
        if (!this.isAutoSearchableTaxiQuery) {
            this.debouncedTaxiSearch.cancel();
            this.taxiSearchToken++;
            this.taxiRows = [];
            this.isLoadingTaxis = false;
            return;
        }
        this.debouncedTaxiSearch();
    }

    /**
     * @param auto see `searchDriver` — suppresses error toasts for searches the
     * user did not explicitly ask for.
     */
    public async searchTaxis(auto: boolean = false) {
        if (!this.isValidTaxiQuery) return;

        const token = ++this.taxiSearchToken;
        this.isLoadingTaxis = true;
        try {
            const res = await taxiRepository.search({
                number: this.taxiQuery.trim(),
                active: this.activeTaxisOnly ? true : undefined
            });
            if (token !== this.taxiSearchToken) return;
            this.taxiRows = (res.data ?? []).map((row: any) => this.normalizeTaxiRow(row));
        } catch (e) {
            if (token !== this.taxiSearchToken) return;
            if (!auto) this.$notifyErrorSimplified('SEARCH.TAXI.ERROR');
            this.taxiRows = [];
        } finally {
            if (token === this.taxiSearchToken) this.isLoadingTaxis = false;
        }
    }

    public async toggleCancellationOrder() {
        this.cancellationOrder = this.cancellationOrder === 'desc' ? 'asc' : 'desc';
        await this.loadCancellations();
    }

    private async loadCancellations() {
        this.isLoadingCancellations = true;
        try {
            const res = await statisticsRepository.getTaxiCancellations(this.cancellationOrder);
            this.cancellationRows = (res.data ?? []).map((row: any) => this.normalizeCancellationRow(row));
            this.cancellationsLoaded = true;
        } catch (e) {
            this.$notifyErrorSimplified('SEARCH.CANCELLATIONS.ERROR');
            this.cancellationRows = [];
        } finally {
            this.isLoadingCancellations = false;
        }
    }

    public async toggleDriverTripOrder() {
        this.driverTripOrder = this.driverTripOrder === 'desc' ? 'asc' : 'desc';
        await this.loadDriverTrips();
    }

    private async loadDriverTrips() {
        this.isLoadingDriverTrips = true;
        try {
            const res = await statisticsRepository.getDriverTrips(this.driverTripOrder);
            this.driverTripRows = (res.data ?? []).map((row: any) => this.normalizeDriverTripRow(row));
            this.driverTripsLoaded = true;
        } catch (e) {
            this.$notifyErrorSimplified('SEARCH.TOP_DRIVERS.ERROR');
            this.driverTripRows = [];
        } finally {
            this.isLoadingDriverTrips = false;
        }
    }

    public async toggleDriverCancellationOrder() {
        this.driverCancellationOrder = this.driverCancellationOrder === 'desc' ? 'asc' : 'desc';
        await this.loadDriverCancellations();
    }

    private async loadDriverCancellations() {
        this.isLoadingDriverCancellations = true;
        try {
            const res = await statisticsRepository.getDriverCancellations(this.driverCancellationOrder);
            this.driverCancellationRows = (res.data ?? []).map((row: any) => this.normalizeDriverCancellationRow(row));
            this.driverCancellationsLoaded = true;
        } catch (e) {
            this.$notifyErrorSimplified('SEARCH.DRIVER_CANCELLATIONS.ERROR');
            this.driverCancellationRows = [];
        } finally {
            this.isLoadingDriverCancellations = false;
        }
    }

    /**
     * The normalizers below keep the tables robust regardless of whether the
     * statistics endpoints return populated documents or plain aggregates.
     */
    private normalizeTaxiRow(row: any) {
        return {
            taxiNo: row.taxiNo ?? '-',
            plate: row.plate ?? '-',
            seats: row.seats ?? '-',
            type: row.type ?? '-',
            company: row.company?.name ?? '-'
        };
    }

    private normalizeCancellationRow(row: any) {
        const taxi = row.taxi && typeof row.taxi === 'object' ? row.taxi : row;
        return {
            taxiNo: taxi.taxiNo ?? '-',
            privateDrive: taxi.privateDrive ?? '-',
            plate: taxi.plate ?? '-',
            company: taxi.company?.name ?? row.company?.name ?? '-',
            cancellations: row.cancellations ?? row.cancellationCount ?? row.count ?? 0
        };
    }

    private normalizeDriverTripRow(row: any) {
        const driver = row.driver && typeof row.driver === 'object' ? row.driver : row;
        const taxi = row.taxi && typeof row.taxi === 'object' ? row.taxi : {};
        const name = driver.name ?? driver.fullName ?? `${driver.firstName ?? ''} ${driver.lastName ?? ''}`.trim();
        const phone = driver.phone?.phoneNumber
            ? `${driver.phone.prefix ?? ''} ${driver.phone.phoneNumber}`.trim()
            : '-';
        return {
            serialId: driver.serialId ?? '-',
            name: name || '-',
            phone,
            company: driver.company?.name ?? row.company?.name ?? '-',
            taxiNo: taxi.taxiNo ?? '-',
            privateDrive: taxi.privateDrive ?? '-',
            trips: row.trips ?? row.tripCount ?? row.rideCount ?? row.count ?? 0
        };
    }

    private normalizeDriverCancellationRow(row: any) {
        const driver = row.driver && typeof row.driver === 'object' ? row.driver : row;
        const taxi = row.taxi && typeof row.taxi === 'object' ? row.taxi : {};
        const name = driver.name ?? driver.fullName ?? `${driver.firstName ?? ''} ${driver.lastName ?? ''}`.trim();
        const phone = driver.phone?.phoneNumber
            ? `${driver.phone.prefix ?? ''} ${driver.phone.phoneNumber}`.trim()
            : '-';
        return {
            serialId: driver.serialId ?? '-',
            name: name || '-',
            phone,
            company: driver.company?.name ?? row.company?.name ?? '-',
            taxiNo: taxi.taxiNo ?? '-',
            privateDrive: taxi.privateDrive ?? '-',
            cancellations: row.cancellations ?? row.cancellationCount ?? row.count ?? 0
        };
    }
}
